import { useEffect, useState } from 'preact/hooks';
import {
  authorityOfferFrom,
  fileAskFromOffer,
  getRecordTypeSchema,
  getRecord,
  getTeamMe,
  listAllRecordsWithLinks,
  listAttentionItems,
  resolveAttentionItem,
  updateRecord,
  verifyProfileTicket,
  ApiError,
  type LinkRow,
  type RecordRow,
  type RecordTypeSchema,
  type TeamMember,
} from '../lib/api';
import { routePath } from '../routes/route';
import { bumpRecordsVersion, openCreateMenu, openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import { canEditTechs } from './Techs';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import './TechDetail.css';

// S4 — THE TECH SCREEN (wf02 §01b, tech()): the first real authority
// split, and the one that must be REAL.
//
// THE AUTHORITY RULE. The role read (GET /api/team/me) chooses only what
// to DRAW — Edit for the office, the locked "Request a change" for
// everyone else, and the pay mask. The WALL is the server's: every write
// goes through the records door, whose authority gate refuses what
// exceeds you with C5e's signed offer. "Request a change" is that
// conversion end to end — attempt the write, read the 409's offer with
// authorityOfferFrom, file it with fileAskFromOffer. The cascade stops
// here and converts (decision 08547e14). Never a client-side guess about
// who may do what, and never a button that pretends.
//
// THE MASK IS THE SERVER'S REFUSAL (S8 — this was the chunk's whole
// point). Pay no longer rides hvac_tech_profile at all: it lives on
// hvac_pay_profile, min_role_read 'admin', and this screen shows values
// exactly when GET /records?type_key=hvac_pay_profile SUCCEEDED and the
// mask exactly when the server answered 403. No role read ever decides
// masking here — `payState` below is nothing but "did the server give it
// to me". (S4's ‘••••’ was a curtain over data the browser already held;
// that finding is closed by the split, not by better curtains.)
//
// THE VERIFY (S8's second half): P-B shipped the office's gated ticket
// verify (POST /api/profiles/:id/tickets/:tid/verify) and nothing called
// it — Today listed expiring tickets nobody could act on. The Tickets
// block below is that affordance. The button shows for EVERYONE: a
// holder's click verifies; a non-holder's click is converted by the
// server's 409 into C5e's signed offer and filed as an ask — never a
// dead button, never a client-side guess about who may verify.
//
// S10 — THE PAY EDITOR, AND WHY IT APPEARS WHERE IT DOES (Huan's decision,
// 2026-08-12). S8 closed the exposure and left the loop open in its own
// words: the admin had the real routed-to-Alan ask, but Alan had no UI door
// to edit pay at all. This is that door — and it obeys the SAME rule the
// mask obeys, because they are the same rule. `payState` is "did the server
// give pay to me"; the editor is offered exactly when the server returned
// the pay record, never from a role read. So an admin (who holds the read)
// sees it and their save converts into the ask routed to Alan by name; a
// dispatcher and a tech never see the block at all, because the server never
// gave them the record to edit. Nothing here decides who may write — the
// gate does, on hvac_pay_profile's (update -> rexx.pay_basis.change) alias.
//
// THE EDIT WRITES THE PAY RECORD, not the profile: pay does not live on
// hvac_tech_profile any more, and a PATCH there would neither find the
// fields nor derive the pay action.
//
// ---------------------------------------------------------------------
// S16 — FOUR FAN-OUTS RETIRE, AND THREE CAPS WITH THEM.
//
// This screen was the app's most expensive read after the work-order spine,
// and none of it was necessary once S15 gave `GET /records` its
// `?include=links`:
//
//   the week      one request per visit (WEEK_JOIN_CAP 60, past which the
//                 week block simply disappeared)      -> hvac_visit
//                 + [tech_profile], one request per 200
//   the pay       when the tech's own link page was truncated, up to
//                 PAY_SCAN_CAP (150) reads walking pay records looking for
//                 the one linked here                 -> hvac_pay_profile
//                 + [pay_tech_profile], ONE request
//   the receipts  the pending count was abandoned whenever the tech's link
//                 page hit LINK_PAGE                  -> hvac_receipt
//                 + [receipt_tech_profile], ONE request
//   jobs done     same, and for the same reason       -> the same visit read
//
// All three caps are deleted. Every number on this screen is now exact for
// a tech of any tenure, because each join is read from the side that
// carries ONE link back here, not from a fifty-long page of the profile's
// own links that fills up oldest-first with the very history the numbers
// are about.
//
// THE HONEST STATES ALL SURVIVE, re-anchored to real failures: the pay mask
// is still and only the server's 403; `receiptsPending === null` still
// renders nothing rather than a zero, but now only when the receipts read
// itself failed; and the week block still disappears when the week cannot
// be read, rather than when it is merely large.
//
// Deactivate, never delete: this screen still offers no Retire of its own.
// S10 put Retire on the shared RecordActions block, which this screen reaches
// through the generic editor (Edit -> RecordEditor), so retiring a technician
// is one screen away rather than absent. A dedicated affordance here is a
// design call, not a gap in the plumbing.

export interface TechDetailProps {
  tenantId: string;
  id: string;
  onNavigate: (path: string) => void;
}

type LoadState = 'loading' | 'ready' | 'error';

const TECH_TYPE = 'hvac_tech_profile';
const PAY_TYPE = 'hvac_pay_profile';
const PAY_LINK = 'pay_tech_profile';
// The ticket-expiry sweep's own dedup convention (core ops/ticketExpiry.ts:
// `credential_expiring:ticket:<recordId>:<ticketId>`) — how a successful
// verify finds the attention item it just answered.
const TICKET_DEDUP_PREFIX = 'credential_expiring:ticket:';
const VISIT_TYPE = 'hvac_visit';
const RECEIPT_TYPE = 'hvac_receipt';
const RECEIPT_LINK = 'receipt_tech_profile';
const VISIT_TECH_LINK = 'tech_profile';

type AskPanel =
  | { kind: 'closed' }
  | { kind: 'open' }
  | { kind: 'busy' }
  | { kind: 'requested' }
  | { kind: 'changed' }
  | { kind: 'refused' }
  | { kind: 'error'; message: string };

function money(n: number): string {
  return n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function textField(r: RecordRow, name: string): string {
  const v = r.fields[name];
  return typeof v === 'string' ? v : '';
}

function numField(r: RecordRow, name: string): number | null {
  const v = r.fields[name];
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function ticketNames(r: RecordRow): string {
  const raw = r.fields['tickets'];
  const arr = Array.isArray(raw) ? raw : [];
  return arr
    .map((t) => (t && typeof t === 'object' && typeof (t as { name?: unknown }).name === 'string' ? (t as { name: string }).name : ''))
    .filter(Boolean)
    .join(' · ');
}

/** One stored ticket entry, as much of it as the verify block needs —
 * the firstRun.ts contract shape ({ id, name, expires_on?, verified_by,
 * verified_at }), read defensively off the json field. An entry without
 * an id cannot be verified through the route (it names tickets by id), so
 * it renders without the button rather than with one that 404s. */
interface TicketRow {
  id: string | null;
  name: string;
  expires_on: string | null;
  verified_by: string | null;
}

function ticketRows(r: RecordRow): TicketRow[] {
  const raw = r.fields['tickets'];
  if (!Array.isArray(raw)) return [];
  const rows: TicketRow[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const t = entry as { id?: unknown; name?: unknown; expires_on?: unknown; verified_by?: unknown };
    rows.push({
      id: typeof t.id === 'string' && t.id.length > 0 ? t.id : null,
      name: typeof t.name === 'string' && t.name.length > 0 ? t.name : 'ticket',
      expires_on: typeof t.expires_on === 'string' && t.expires_on.length > 0 ? t.expires_on : null,
      verified_by: typeof t.verified_by === 'string' && t.verified_by.length > 0 ? t.verified_by : null,
    });
  }
  return rows;
}

/** "Did the server give pay to me" — the WHOLE of the masking decision.
 * 'held' means GET /records?type_key=hvac_pay_profile succeeded (record
 * is null when no pay record is linked yet); 'withheld' means the server
 * answered 403 — the reader's ladder rung is below the type. */
type PayState = { kind: 'held'; record: RecordRow | null } | { kind: 'withheld' };

/** S10 — the pay editor's own small state. `saved` and `requested` are the
 * two honest endings: the change landed, or it became an ask addressed to
 * Alan. Same vocabulary as AskPanel above, for the same reason. */
type PayEditState =
  | { kind: 'closed' }
  | { kind: 'open' }
  | { kind: 'busy' }
  | { kind: 'saved' }
  | { kind: 'requested' }
  | { kind: 'refused' }
  | { kind: 'error'; message: string };

type VerifyState =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'requested' }
  | { kind: 'refused' }
  | { kind: 'error'; message: string };

function visitWhen(v: RecordRow): string | null {
  const f = v.fields;
  const raw = f['actual_end'] ?? f['actual_start'] ?? f['scheduled_start'];
  return typeof raw === 'string' && raw.length > 0 ? raw : null;
}

function startOfWeek(now: Date): Date {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = d.getDay();
  // Monday-start week.
  d.setDate(d.getDate() - ((day + 6) % 7));
  return d;
}

function isToday(iso: string): boolean {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()
  );
}

function timeOf(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** Presentation only, like canEditTechs — the gate is the wall. */
function canSchedule(member: TeamMember): boolean {
  return (
    member.role === 'owner' || member.jobRoles.includes('dispatcher') || member.jobRoles.includes('owner')
  );
}

interface WeekView {
  joined: boolean;
  todayJoined: boolean;
  myToday: RecordRow[];
  myWeek: RecordRow[];
  /** Every completed visit ever linked to this tech — exact now that the
   *  count comes from the visit side rather than the profile's link page. */
  completed: number | null;
}

/** The other end of a relationship, whichever side of the link row this
 *  record sits on. */
function linkedId(links: LinkRow[], selfId: string, linkType: string): string | null {
  for (const l of links) {
    if (l.link_type !== linkType) continue;
    if (l.src_id === selfId) return l.dst_id;
    if (l.dst_id === selfId) return l.src_id;
  }
  return null;
}

export function TechDetail({ tenantId, id, onNavigate }: TechDetailProps) {
  const [load, setLoad] = useState<LoadState>('loading');
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [tech, setTech] = useState<RecordRow | null>(null);
  const [member, setMember] = useState<TeamMember | null>(null);
  const [schema, setSchema] = useState<RecordTypeSchema | null>(null);
  const [week, setWeek] = useState<WeekView>({
    joined: false,
    todayJoined: false,
    myToday: [],
    myWeek: [],
    completed: null,
  });
  const [receiptsPending, setReceiptsPending] = useState<number | null>(null);
  const [ask, setAsk] = useState<AskPanel>({ kind: 'closed' });
  const [askField, setAskField] = useState('');
  const [askValue, setAskValue] = useState('');
  const [pay, setPay] = useState<PayState>({ kind: 'withheld' });
  const [payEdit, setPayEdit] = useState<PayEditState>({ kind: 'closed' });
  const [payHourly, setPayHourly] = useState('');
  const [payCommission, setPayCommission] = useState('');
  const [verify, setVerify] = useState<Record<string, VerifyState>>({});
  const recordsVersion = useRecordsVersion();

  // The ask receipt survives the reload its own success triggers — it
  // resets only when the screen moves to a DIFFERENT technician.
  useEffect(() => {
    setAsk({ kind: 'closed' });
    setAskField('');
    setAskValue('');
    setVerify({});
    setPayEdit({ kind: 'closed' });
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    async function loadAll() {
      setLoad('loading');
      setFailure(null);
      try {
        // SIX reads for the whole screen, whatever this technician's tenure.
        // Each join is read from the side that carries ONE link back here —
        // a visit names its tech, a pay record names its profile, a receipt
        // names whose it is. Which is why the profile itself is now read
        // WITHOUT its links: nothing on this screen comes off that page any
        // more, so there is nothing left for its oldest-first cut-off to
        // quietly shorten.
        const [profile, me, sch, visitLinks, receiptLinks] = await Promise.all([
          getRecord(tenantId, id),
          getTeamMe(tenantId),
          getRecordTypeSchema(tenantId, TECH_TYPE),
          listAllRecordsWithLinks(tenantId, VISIT_TYPE, { linkTypes: [VISIT_TECH_LINK] }),
          listAllRecordsWithLinks(tenantId, RECEIPT_TYPE, { linkTypes: [RECEIPT_LINK] }),
        ]);

        // Which visits are THIS tech's — read off each visit, exactly, with
        // no cap. A visit the server would not answer links for is not
        // claimed either way: it is neither counted as theirs nor as
        // somebody else's.
        const visitUnanswered = new Set([...visitLinks.unreadable, ...visitLinks.truncated]);
        const mine = visitLinks.records.filter((v) => {
          if (visitUnanswered.has(v.id)) return false;
          const vLinks = visitLinks.linksByRecordId[v.id];
          if (vLinks === undefined) return false;
          return linkedId(vLinks, v.id, VISIT_TECH_LINK) === id;
        });

        const weekStart = startOfWeek(new Date());
        const myWeek = mine.filter((v) => {
          const when = visitWhen(v);
          return when !== null && new Date(when).getTime() >= weekStart.getTime();
        });
        const myToday = myWeek.filter((v) => {
          const when = visitWhen(v);
          return when !== null && isToday(when);
        });

        // S8 — PAY, FROM THE PAY TYPE, AND THE SERVER DECIDES. One list
        // call answers both questions at once: a 403 IS the mask (the
        // reader's rung is below hvac_pay_profile's min_role_read), and a
        // success carries every pay record with the link that says whose it
        // is. S16: that link now rides the list, so the profile's own page
        // is never consulted and the 150-record scan behind it is gone.
        let payState: PayState;
        try {
          const pays = await listAllRecordsWithLinks(tenantId, PAY_TYPE, { linkTypes: [PAY_LINK] });
          const payUnanswered = new Set([...pays.unreadable, ...pays.truncated]);
          const payRecord =
            pays.records.find((p) => {
              if (payUnanswered.has(p.id)) return false;
              const pLinks = pays.linksByRecordId[p.id];
              if (pLinks === undefined) return false;
              return linkedId(pLinks, p.id, PAY_LINK) === id;
            }) ?? null;
          payState = { kind: 'held', record: payRecord };
        } catch (err) {
          if (err instanceof ApiError && err.status === 403) payState = { kind: 'withheld' };
          else throw err;
        }

        // Receipts pending — exact, from the receipt side. It used to be
        // abandoned entirely whenever the tech's link page filled up, which
        // is precisely when a long-serving tech has receipts waiting.
        const receiptUnanswered = new Set([...receiptLinks.unreadable, ...receiptLinks.truncated]);
        const pending = receiptLinks.records.filter((r) => {
          if (receiptUnanswered.has(r.id)) return false;
          const rLinks = receiptLinks.linksByRecordId[r.id];
          if (rLinks === undefined) return false;
          if (linkedId(rLinks, r.id, RECEIPT_LINK) !== id) return false;
          return r.lifecycle_state === 'submitted';
        }).length;

        if (cancelled) return;
        setTech(profile);
        setPay(payState);
        setMember(me.member);
        setSchema(sch);
        setWeek({
          joined: true,
          todayJoined: true,
          myToday,
          myWeek,
          completed: mine.filter((v) => v.lifecycle_state === 'completed').length,
        });
        setReceiptsPending(pending);
        setLoad('ready');
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setLoad('error');
      }
    }
    void loadAll();
    return () => {
      cancelled = true;
    };
  }, [tenantId, id, recordsVersion, retryNonce]);

  const crumb = (current: string) => (
    <nav class="tech-detail__crumb" aria-label="Breadcrumb">
      <button type="button" onClick={() => onNavigate(routePath({ screen: 'today' }))}>
        Ether Inc
      </button>
      <span class="tech-detail__crumb-sep" aria-hidden="true">
        ›
      </span>
      <button type="button" onClick={() => onNavigate(routePath({ screen: 'techs' }))}>
        Techs
      </button>
      <span class="tech-detail__crumb-sep" aria-hidden="true">
        ›
      </span>
      <span class="tech-detail__crumb-cur">{current}</span>
    </nav>
  );

  if (load === 'loading') {
    return (
      <div class="tech-detail">
        {crumb('…')}
        <div class="tech-detail__say">One moment…</div>
      </div>
    );
  }

  if (load === 'error' && failure !== null) {
    return (
      <div class="tech-detail">
        {crumb('…')}
        <div class="tech-detail__say">
          {failure.headline}
          <br />
          <span class="tech-detail__say-soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button type="button" class="tech-detail__retry" onClick={() => setRetryNonce((n) => n + 1)}>
            Read it again
          </button>
        ) : null}
      </div>
    );
  }

  if (!tech || !member) return null;

  const editable = canEditTechs(member);
  const scheduler = canSchedule(member);

  const role = textField(tech, 'role');
  const tickets = ticketNames(tech);
  const sub = [role, tickets].filter(Boolean).join(' · ');
  // S8: pay comes off the PAY record the server chose to return — never
  // off the profile (which no longer carries it) and never off a role.
  const payHeld = pay.kind === 'held';
  // S10: THE editor's whole precondition. Not a role — the record the
  // server chose to return. Null means either the mask (withheld) or an
  // office reader with nothing on file yet; neither is editable here.
  const payRecord = pay.kind === 'held' ? pay.record : null;
  const hourly = payRecord ? numField(payRecord, 'hourly_rate') : null;
  const commission = payRecord ? numField(payRecord, 'commission_rate') : null;
  const ticketList = ticketRows(tech);
  const phone = textField(tech, 'phone');
  const email = textField(tech, 'email');

  const weekHours = week.myWeek.reduce((sum, v) => sum + (numField(v, 'hours_actual') ?? 0), 0);
  const weekRevenue = week.myWeek.reduce((sum, v) => sum + (numField(v, 'billed_amount') ?? 0), 0);

  // Jobs completed (all time). S16: exact for any tenure — counted from the
  // visits' own tech link rather than from the profile's oldest-first link
  // page, which used to abandon the number for exactly the long-serving
  // technicians it is about.
  const jobsCompleted = week.completed;

  // The request-a-change form's field vocabulary: the profile's own
  // schema, writable value shapes only. The picker deliberately INCLUDES
  // pay — asking for a pay change is exactly what the ask exists for.
  const askableFields = (schema?.fields ?? []).filter(
    (f) => (f.type === 'text' || f.type === 'number' || f.type === 'currency') && f.name !== 'external_id'
  );

  async function submitAsk(e: Event) {
    e.preventDefault();
    if (ask.kind === 'busy') return;
    const field = askField || askableFields[0]?.name;
    if (!field || !askValue.trim()) return;
    const def = askableFields.find((f) => f.name === field);
    let value: unknown = askValue.trim();
    if (def && (def.type === 'number' || def.type === 'currency')) {
      const n = Number(askValue.trim());
      if (!Number.isFinite(n)) {
        setAsk({ kind: 'error', message: 'That needs a number.' });
        return;
      }
      value = n;
    }
    setAsk({ kind: 'busy' });
    try {
      // The attempt IS the mechanism: if this session holds the
      // authority, the change simply lands; if not, the server's 409
      // carries the signed offer and the ask is filed from it.
      await updateRecord(tenantId, id, { [field]: value });
      setAsk({ kind: 'changed' });
      bumpRecordsVersion();
    } catch (err) {
      const offer = authorityOfferFrom(err);
      if (offer?.fileAsk?.available && offer.fileAsk.offerToken) {
        try {
          await fileAskFromOffer(tenantId, offer.fileAsk.offerToken);
          setAsk({ kind: 'requested' });
        } catch {
          setAsk({ kind: 'error', message: 'That didn’t go through. Try again.' });
        }
      } else if (offer) {
        setAsk({ kind: 'refused' });
      } else {
        setAsk({
          kind: 'error',
          message: err instanceof ApiError ? err.message : 'That didn’t go through. Try again.',
        });
      }
    }
  }

  /** The verify, end to end: the attempt IS the mechanism (same shape as
   * submitAsk above). A holder's click verifies and then clears the
   * expiry attention item the sweep raised for exactly this ticket
   * (dedup `credential_expiring:ticket:<recordId>:<ticketId>`, resolved
   * through the same door Today resolves through). A non-holder's click
   * is converted by the server's 409 into C5e's signed offer and filed
   * as an ask — the button is never dead and never guesses. */
  async function verifyTicket(ticketId: string) {
    const current = verify[ticketId]?.kind;
    if (current === 'busy') return;
    setVerify((v) => ({ ...v, [ticketId]: { kind: 'busy' } }));
    try {
      await verifyProfileTicket(tenantId, id, ticketId);
      let next: VerifyState = { kind: 'idle' };
      try {
        const items = await listAttentionItems(tenantId);
        const dedup = `${TICKET_DEDUP_PREFIX}${id}:${ticketId}`;
        const item = items.find((it) => it.dedupKey === dedup && it.resolvedAt === null);
        if (item) await resolveAttentionItem(tenantId, item.id);
      } catch {
        // The verify LANDED (the reload below will show it); only the
        // attention clear failed — say so rather than pretending.
        next = { kind: 'error', message: 'Verified — but its attention item did not clear.' };
      }
      setVerify((v) => ({ ...v, [ticketId]: next }));
      bumpRecordsVersion();
    } catch (err) {
      const offer = authorityOfferFrom(err);
      if (offer?.fileAsk?.available && offer.fileAsk.offerToken) {
        try {
          await fileAskFromOffer(tenantId, offer.fileAsk.offerToken);
          setVerify((v) => ({ ...v, [ticketId]: { kind: 'requested' } }));
        } catch {
          setVerify((v) => ({ ...v, [ticketId]: { kind: 'error', message: 'That didn’t go through. Try again.' } }));
        }
      } else if (offer) {
        setVerify((v) => ({ ...v, [ticketId]: { kind: 'refused' } }));
      } else {
        setVerify((v) => ({
          ...v,
          [ticketId]: {
            kind: 'error',
            message: err instanceof ApiError ? err.message : 'That didn’t go through. Try again.',
          },
        }));
      }
    }
  }

  /** Seeds the two inputs from the record the server actually returned, so
   * the form opens showing what is on file rather than empty. */
  function openPayEdit() {
    setPayHourly(hourly === null ? '' : String(hourly));
    setPayCommission(commission === null ? '' : String(commission));
    setPayEdit({ kind: 'open' });
  }

  /** The pay write, end to end — the attempt IS the mechanism, exactly as
   * submitAsk and verifyTicket above. Alan's edit simply lands. An admin's
   * edit is refused by the gate on (hvac_pay_profile, update) ->
   * rexx.pay_basis.change, whose 409 carries C5e's signed offer routed to
   * the owner BY NAME; it is filed from that offer and Alan answers it in
   * his inbox. Nothing here checks a role, and nothing here writes the
   * tech profile. */
  async function submitPay(e: Event) {
    e.preventDefault();
    if (payEdit.kind === 'busy' || !payRecord) return;

    const fields: Record<string, unknown> = {};
    for (const [name, raw] of [
      ['hourly_rate', payHourly],
      ['commission_rate', payCommission],
    ] as const) {
      const trimmed = raw.trim();
      if (trimmed === '') {
        // Cleared means cleared — neither field is required, and sending
        // null is how the record stops carrying a value.
        fields[name] = null;
        continue;
      }
      const n = Number(trimmed);
      if (!Number.isFinite(n)) {
        setPayEdit({ kind: 'error', message: 'That needs a number.' });
        return;
      }
      fields[name] = n;
    }

    setPayEdit({ kind: 'busy' });
    try {
      await updateRecord(tenantId, payRecord.id, fields);
      setPayEdit({ kind: 'saved' });
      bumpRecordsVersion();
    } catch (err) {
      const offer = authorityOfferFrom(err);
      if (offer?.fileAsk?.available && offer.fileAsk.offerToken) {
        try {
          await fileAskFromOffer(tenantId, offer.fileAsk.offerToken);
          setPayEdit({ kind: 'requested' });
        } catch {
          setPayEdit({ kind: 'error', message: 'That didn’t go through. Try again.' });
        }
      } else if (offer) {
        setPayEdit({ kind: 'refused' });
      } else {
        setPayEdit({
          kind: 'error',
          message: err instanceof ApiError ? err.message : 'That didn’t go through. Try again.',
        });
      }
    }
  }

  const askOpen = ask.kind === 'open' || ask.kind === 'busy' || ask.kind === 'error';
  const payFormOpen = payEdit.kind === 'open' || payEdit.kind === 'busy' || payEdit.kind === 'error';

  return (
    <div class="tech-detail">
      {crumb(tech.display_name)}

      <div class="tech-detail__hero">
        <div>
          <div class="tech-detail__eyebrow">Technician</div>
          <h2 class="tech-detail__title">{tech.display_name}</h2>
          {sub ? <div class="tech-detail__sub">{sub}</div> : null}
        </div>
        {editable ? (
          <button
            type="button"
            class="tech-detail__edit"
            onClick={() => openRecordEditor({ mode: 'edit', typeKey: TECH_TYPE, id })}
          >
            Edit
          </button>
        ) : ask.kind === 'requested' ? (
          // S11: the receipt is now a DOORWAY. "Sent to the back office"
          // with nowhere to look was an acknowledgement; the filed-by-me
          // inbox is where the answer (and a decline's reason) shows up.
          <button
            type="button"
            class="tech-detail__locked tech-detail__locked--done"
            onClick={() => onNavigate(routePath({ screen: 'asks', view: 'filed' }))}
          >
            ✓ Requested — sent to the back office ›
          </button>
        ) : ask.kind === 'changed' ? (
          <span class="tech-detail__locked tech-detail__locked--done">✓ Changed</span>
        ) : (
          <button
            type="button"
            class="tech-detail__locked"
            onClick={() => setAsk({ kind: 'open' })}
          >
            🔒 Request a change
          </button>
        )}
      </div>

      {askOpen ? (
        <form class="tech-detail__ask-form" onSubmit={submitAsk}>
          <select
            class="tech-detail__ask-select"
            value={askField || askableFields[0]?.name || ''}
            onChange={(e) => setAskField((e.currentTarget as HTMLSelectElement).value)}
            aria-label="Field"
          >
            {askableFields.map((f) => (
              <option value={f.name} key={f.name}>
                {f.label}
              </option>
            ))}
          </select>
          <input
            class="tech-detail__ask-input"
            type="text"
            value={askValue}
            onInput={(e) => setAskValue((e.currentTarget as HTMLInputElement).value)}
            aria-label="New value"
          />
          <button type="submit" class="tech-detail__ask-send" disabled={ask.kind === 'busy'}>
            {ask.kind === 'busy' ? 'Sending…' : 'Request it'}
          </button>
          <button type="button" class="tech-detail__ask-cancel" onClick={() => setAsk({ kind: 'closed' })}>
            Cancel
          </button>
          {ask.kind === 'error' ? (
            <span class="tech-detail__ask-error" role="status">
              {ask.message}
            </span>
          ) : null}
        </form>
      ) : null}
      {ask.kind === 'refused' ? (
        <div class="tech-detail__ask-error" role="status">
          That change is outside your authority, and the office can't be asked automatically for it.
        </div>
      ) : null}

      <div class="tech-detail__cols">
        <div>
          <div class="tech-detail__sec">
            <span>Today</span>
            <span class="tech-detail__mini">{scheduler ? 'you may schedule' : 'view only'}</span>
          </div>
          {!week.todayJoined ? (
            <div class="tech-detail__empty">—</div>
          ) : week.myToday.length === 0 ? (
            <div class="tech-detail__empty">Nothing booked today.</div>
          ) : (
            week.myToday.map((v) => {
              const when = visitWhen(v);
              const line =
                textField(v, 'work_done') || v.display_name;
              return (
                <div class="tech-detail__kv" key={v.id}>
                  <span>{when ? timeOf(when) : '—'}</span>
                  <b class="tech-detail__kv-soft tech-detail__kv-right">{line}</b>
                </div>
              );
            })
          )}

          {week.joined ? (
            <>
              <div class="tech-detail__sec tech-detail__sec--later">
                <span>This week</span>
              </div>
              <div class="tech-detail__kv">
                <span>Hours</span>
                <b>{weekHours.toFixed(1)}</b>
              </div>
              <div class="tech-detail__kv">
                <span>Visits</span>
                <b>{week.myWeek.length}</b>
              </div>
              <div class="tech-detail__kv">
                <span>Revenue billed</span>
                <b>${money(weekRevenue)}</b>
              </div>
            </>
          ) : null}
        </div>

        <div>
          <div class="tech-detail__sec">
            <span>Pay</span>
            <span class="tech-detail__secact">
              <span class="tech-detail__mini">{payHeld ? 'admin' : 'locked'}</span>
              {/* S10: offered on the SAME basis as the values themselves —
                  the server returned the pay record. */}
              {payRecord && !payFormOpen ? (
                payEdit.kind === 'requested' ? (
                  <button
                    type="button"
                    class="tech-detail__mini tech-detail__receipt"
                    onClick={() => onNavigate(routePath({ screen: 'asks', view: 'filed' }))}
                  >
                    ✓ Requested — sent to Alan ›
                  </button>
                ) : payEdit.kind === 'saved' ? (
                  <span class="tech-detail__mini">✓ Changed</span>
                ) : (
                  <button type="button" class="tech-detail__paychange" onClick={openPayEdit}>
                    Change
                  </button>
                )
              ) : null}
            </span>
          </div>
          {payFormOpen ? (
            // The form REPLACES the two value rows in place rather than
            // opening a panel beside them: the rows already name the fields,
            // so editing needs no second set of labels and no caption.
            <form class="tech-detail__pay-form" onSubmit={submitPay}>
              <div class="tech-detail__kv">
                <label for="pay-hourly">Hourly rate</label>
                <input
                  id="pay-hourly"
                  class="tech-detail__pay-input"
                  type="text"
                  inputMode="decimal"
                  value={payHourly}
                  onInput={(e) => setPayHourly((e.currentTarget as HTMLInputElement).value)}
                />
              </div>
              <div class="tech-detail__kv">
                <label for="pay-commission">Commission rate</label>
                <input
                  id="pay-commission"
                  class="tech-detail__pay-input"
                  type="text"
                  inputMode="decimal"
                  value={payCommission}
                  onInput={(e) => setPayCommission((e.currentTarget as HTMLInputElement).value)}
                />
              </div>
              <div class="tech-detail__pay-acts">
                <button type="submit" class="tech-detail__ask-send" disabled={payEdit.kind === 'busy'}>
                  {payEdit.kind === 'busy' ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  class="tech-detail__ask-cancel"
                  onClick={() => setPayEdit({ kind: 'closed' })}
                >
                  Cancel
                </button>
                {payEdit.kind === 'error' ? (
                  <span class="tech-detail__ask-error" role="status">
                    {payEdit.message}
                  </span>
                ) : null}
              </div>
            </form>
          ) : (
            <>
              <div class="tech-detail__kv">
                <span>Hourly rate</span>
                <b>{payHeld ? (hourly !== null ? `$${money(hourly)}` : '—') : '••••'}</b>
              </div>
              <div class="tech-detail__kv">
                <span>Commission rate</span>
                <b>{payHeld ? (commission !== null ? `${commission}%` : '—') : '••••'}</b>
              </div>
            </>
          )}
          {payEdit.kind === 'refused' ? (
            <div class="tech-detail__ask-error" role="status">
              Changing pay is outside your authority, and Alan can't be asked automatically for it.
            </div>
          ) : null}

          <div class="tech-detail__sec tech-detail__sec--later">
            <span>Tickets &amp; licences</span>
            <span class="tech-detail__mini">office verifies</span>
          </div>
          {ticketList.length === 0 ? (
            <div class="tech-detail__empty">No tickets on file.</div>
          ) : (
            ticketList.map((t, i) => {
              const tid = t.id;
              const key = tid ?? `pos-${i}`;
              const vs: VerifyState = (tid ? verify[tid] : undefined) ?? { kind: 'idle' };
              return (
                <div key={key}>
                  <div class="tech-detail__kv">
                    <span>
                      {t.name}
                      {t.expires_on ? ` · to ${t.expires_on}` : ''}
                    </span>
                    {t.verified_by ? (
                      <b class="tech-detail__kv-soft">✓ Verified</b>
                    ) : vs.kind === 'requested' ? (
                      <button
                        type="button"
                        class="tech-detail__kv-soft tech-detail__receipt"
                        onClick={() => onNavigate(routePath({ screen: 'asks', view: 'filed' }))}
                      >
                        ✓ Requested — sent to the back office ›
                      </button>
                    ) : tid ? (
                      <button
                        type="button"
                        class="tech-detail__verify"
                        disabled={vs.kind === 'busy'}
                        onClick={() => void verifyTicket(tid)}
                      >
                        {vs.kind === 'busy' ? 'Verifying…' : 'Verify'}
                      </button>
                    ) : (
                      <b class="tech-detail__kv-soft">—</b>
                    )}
                  </div>
                  {vs.kind === 'refused' ? (
                    <div class="tech-detail__ask-error" role="status">
                      Verifying is outside your authority, and the office can't be asked automatically for it.
                    </div>
                  ) : vs.kind === 'error' ? (
                    <div class="tech-detail__ask-error" role="status">
                      {vs.message}
                    </div>
                  ) : null}
                </div>
              );
            })
          )}

          {jobsCompleted !== null ? (
            <>
              <div class="tech-detail__sec tech-detail__sec--later">
                <span>Quality</span>
              </div>
              <div class="tech-detail__kv">
                <span>Jobs completed</span>
                <b>{jobsCompleted}</b>
              </div>
            </>
          ) : null}

          <div class="tech-detail__sec tech-detail__sec--later">
            <span>Contact</span>
          </div>
          {phone || email ? (
            <>
              {phone ? (
                <div class="tech-detail__kv">
                  <span>Phone</span>
                  <b class="tech-detail__kv-soft">{phone}</b>
                </div>
              ) : null}
              {email ? (
                <div class="tech-detail__kv">
                  <span>Email</span>
                  <b class="tech-detail__kv-soft">{email}</b>
                </div>
              ) : null}
            </>
          ) : (
            <div class="tech-detail__empty">No contact details yet.</div>
          )}
        </div>
      </div>

      {!editable ? (
        <div class="tech-detail__askline">
          <span class="tech-detail__askline-stripe" />
          <span>
            <b>Pay and tickets are outside your authority.</b> Request a change and it goes to the
            back office as an ask they accept or decline — the cascade stops here and converts.
          </span>
        </div>
      ) : null}

      <div class="tech-detail__rail">
        <span class="tech-detail__rail-label">About {tech.display_name}</span>
        {week.todayJoined ? (
          <span class="tech-detail__pill tech-detail__pill--on">
            <b>{week.myToday.length}</b> Today
          </span>
        ) : null}
        {week.joined ? (
          <span class="tech-detail__pill">
            <b>{week.myWeek.length}</b> Visits this week
          </span>
        ) : null}
        {receiptsPending !== null ? (
          <span class="tech-detail__pill">
            <b>{receiptsPending}</b> Receipts pending
          </span>
        ) : null}
        {editable ? (
          <button type="button" class="tech-detail__pill tech-detail__pill--add" onClick={openCreateMenu}>
            + Add
          </button>
        ) : null}
      </div>
    </div>
  );
}
